/**
 * @file The player's facing marker: a neon arrow anchored to the player mesh.
 *
 * Built as a pivot with the cone parked off to one side rather than a cone at
 * the mesh origin, so the arrow both points where the body is looking and sits
 * on that side of the player. The pivot lives at knee height, well below the
 * barrel, so the floor readability cue never gets confused with the weapon
 * aim line.
 *
 * Only geometry lives here; {@link ../systems/ExitDirectionSystem.js} is what
 * turns it every frame.
 */

import * as THREE from 'three';
import { PALETTE } from '../config/Palette.js';

/** Name the pivot is looked up by, from the player's mesh. */
export const EXIT_ARROW_NAME = 'exitArrow';

/**
 * Builds the facing arrow pivot. The pivot's local `+Z` follows the player
 * mesh, so the system only has to show or hide it.
 *
 * @param {import('./MeshFactory.js').MeshFactory} factory Geometry/material cache.
 * @param {import('../config/GameConfig.js').GameConfig['player']} player Player tunables.
 * @returns {THREE.Object3D} Pivot, ready to be added to the player mesh.
 */
export function buildExitArrow(factory, player) {
  const cone = new THREE.Mesh(
    factory.geometry('player:exitArrow', () => {
      const geometry = new THREE.ConeGeometry(0.12, 0.4, 8);
      // Cones point up +Y; lay it along +Z so the pivot's heading is its tip.
      geometry.rotateX(Math.PI / 2);
      return geometry;
    }),
    factory.material(
      'player:exitArrow',
      () =>
        new THREE.MeshStandardMaterial({
          color: PALETTE.exitIndicator,
          emissive: PALETTE.exitIndicator,
          emissiveIntensity: 1,
          roughness: 0.36,
          metalness: 0.18,
          // Full intensity like the halo ring, so it survives the tone mapper.
          toneMapped: false
        })
    )
  );
  cone.castShadow = false;
  // Just past the halo ring, tilted up so the isometric camera reads the tip
  // rather than a foreshortened dot.
  cone.position.z = player.radius * 2;
  cone.rotation.x = -0.35;

  const pivot = new THREE.Object3D();
  pivot.name = EXIT_ARROW_NAME;
  pivot.position.y = -player.height * 0.55 + 0.2;
  // Neutral until a live player owns the mesh.
  pivot.visible = false;
  pivot.add(cone);

  return pivot;
}
